/**
 * Enemy Live Plays
 *
 * Synthesizes what looks like a live drag whenever the OPPONENT acts, driven
 * by the server's pre-resolution events (summonStarted, characterActionStarted,
 * spellCastStarted) — agnostic to AI vs human opponents, no pixel trajectories
 * recorded. Because the events fire BEFORE the storyteller resolves, the play
 * animates during the wait: the enemy visibly "makes their move" while the
 * server thinks, and the authoritative snapshot lands the real outcome.
 *
 *  - Board attack/ability: the attacker card glides onto its target and back;
 *    an impact ring + sound land at contact.
 *  - Summon: the accepted card prepares offscreen, then its complete face
 *    survives the immediate matching snapshot and hands off to its settled
 *    authoritative board card without ever exposing a second body.
 *  - Spell: the revealed spell card glides from their hand to its target and
 *    dissolves (the SpellAnnounce banner narrates it in parallel).
 *
 * All visuals render in the pointer-transparent #fx-layer via FX.glidePlay.
 */

import { Board } from './board.js';
import { CardDrag } from './card-drag.js';
import { Cards } from './cards.js';
import { EnemyFan } from './enemy-fan.js';
import { FX } from './fx.js';
import { Tutorial } from './tutorial.js';
import { GameState } from '../game-state.js';
let EnemyReplay = {};

// Create UI namespace if it doesn't exist

EnemyReplay = (function () {
    /* ENEMY ACTION PACING, x1.2 (owner, 2026-08-08): "make enemy player actions
       also last 1.2x longer". The two motions scale together so a travel still
       reads as shorter than the strike it leads into. Was 520 / 460. */
    const ATTACK_DURATION = 624;
    const TRAVEL_DURATION = 552;
    const SUMMON_PREPARATION_TIMEOUT_MS = 2_000;
    const SUMMON_HANDOFF_TIMEOUT_MS = 2_000;
    /* THE ARRIVAL HOLD'S HARD CAP.
       Hiding the authoritative card is honest only while a traveler is standing
       in for it. The board render, however, can land BEFORE the traveler's face
       has decoded, and the hold used to sit open until it did — measured at
       1080ms on the owner's own 844x280 DPR3 page box (opacity 0, arrival
       marker set, zero travelers anywhere in #fx-layer) and codified at up to
       2000ms by the preparation timeout. That is the report: "it dissapears and
       then comes back in a second."
       docs/asset-loading.md permits a user-visible gate only "hard-capped", and
       the repo already owns that cap: `--card-art-grace` (cards.css). One
       number for every art wait in the game. Past it the card is revealed and
       the traveler is abandoned — never flown afterwards, because a clone
       landing on a card the player is already reading takes the description
       away a second time. */
    const SUMMON_ARRIVAL_GRACE_MS = 450;
    /* A PICTURE ALREADY ON THE WIRE IS WAITED FOR, WITH THE CARD STILL IN THE
       HAND (round 17, 28 September 2026). The owner, after enemy summons
       arrived without art: "the animations are not played without they are
       being loaded like it just delays it a bit when it's displayed". A summon
       resolves at once, so its snapshot lands a few frames after
       `summonStarted`, and the grace above ran out long before a cold picture
       (a 722 KB card picture: 0.76 s on the EU phone 4G seat, 1.69 s on the far
       phone seat). The traveller now waits for its face up to the battle's
       arrival cap, counted from the summon (when its picture was asked for),
       but only while the card is honestly somewhere: its back stays in the
       enemy's hand (Cards.holdOpponentDeparture) until the traveller leaves
       from it or the cap lets it go, so nothing vanishes meanwhile. The grace
       stays the floor, and the whole wait for a card with no back to keep (a
       revealed one) or no traveller to fly (reduced motion). */
    const summonArtCapMs = () => {
        const cap = Number(Cards?.ARRIVAL_ART_WAIT_CAP_MS);
        return Number.isFinite(cap) && cap > 0 ? cap : 2000;
    };
    let replayGeneration = 0;
    const preparationHosts = new Set();
    const pendingSummons = new Map();
    const activeSummons = new Map();

    function removePreparationHost(host, content = null) {
        if (content?.parentElement === host) content.remove();
        host?.remove();
        preparationHosts.delete(host);
    }

    function clearPreparationHosts(preserved = new Set()) {
        preparationHosts.forEach((host) => {
            if (preserved.has(host)) return;
            host.remove();
            preparationHosts.delete(host);
        });
    }

    /**
     * Replay only established opponents' plays:
     *  - never my own actions (I performed the real drag),
     *  - never before the game view exists (reconnect hydration),
     *  - recorded tutorial summons/spells stay with the tutorial cue layer;
     *    organic step 999 resumes normal replay (enemy board attacks have no
     *    equivalent cue and callers explicitly keep them visible throughout),
     *  - never in a hidden tab (animations would pile up meaninglessly).
     */
    function shouldReplay(actingPlayerId, { allowDuringTutorial = false } = {}) {
        if (!actingPlayerId) return false;
        const state = (typeof GameState !== 'undefined' && GameState.get) ? GameState.get() : null;
        if (!state || state.gameStarted !== true) return false;
        if (actingPlayerId === state.playerId) return false;
        if (!allowDuringTutorial && Tutorial?.isActive?.()
            && Number(Tutorial.getStep?.()) !== 999) return false;
        if (document.hidden) return false;
        return true;
    }

    function visibleRect(el) {
        if (!el || typeof el.getBoundingClientRect !== 'function') return null;
        const rect = el.getBoundingClientRect();
        return rect && rect.width > 0 && rect.height > 0 ? rect : null;
    }

    /**
     * The box a traveler departs FROM. A fanned hand card is ROTATED, so its
     * bounding rect is the axis-aligned envelope — ~28% too wide with a false
     * aspect at 1400x640 (enemy-fan.css records the measurement). A clone
     * sized from the envelope takes off oversized and, because glide geometry
     * works in centres, lands offset by half the error. Wear the card's
     * LAYOUT box instead, centred where the rotated card visually sits.
     */
    function departureRect(el) {
        const rect = visibleRect(el);
        if (!rect) return null;
        const width = el.offsetWidth;
        const height = el.offsetHeight;
        if (!(width > 0) || !(height > 0)) return rect;
        const left = rect.left + (rect.width - width) / 2;
        const top = rect.top + (rect.height - height) / 2;
        return { left, top, width, height, right: left + width, bottom: top + height };
    }

    function boardCard(instanceId, zone) {
        if (!instanceId) return null;
        try {
            return document.querySelector(`.card[data-instance-id="${instanceId}"][data-zone="${zone}"]`);
        } catch (_) {
            return null;
        }
    }

    const enemyBoardCard = (instanceId) => boardCard(instanceId, 'opponent-board');
    const ownBoardCard = (instanceId) => boardCard(instanceId, 'player-board');

    function myPlayerId() {
        const state = (typeof GameState !== 'undefined' && GameState.get) ? GameState.get() : null;
        return state ? state.playerId : null;
    }

    /**
     * A whole-zone entity (deck pile / hand) is addressed as `${ownerId}_deck`
     * or `${ownerId}_hand`. Resolve it to my side or the opponent's by owner —
     * enemies can aim these at me OR at themselves (self-mill, seeding, etc.).
     */
    function ownerZoneEl(ownerId, zone) {
        const side = ownerId && ownerId === myPlayerId() ? 'player' : 'opponent';
        return document.getElementById(`${side}-${zone}`);
    }

    /**
     * Resolve the drop target of an enemy action to a live element. Targets an
     * enemy can aim at me are equally aimable at itself, so every branch keys
     * off the payload, not "the other player":
     *  - 'card' + board instanceId → that card anywhere on either board,
     *  - 'card' + `${owner}_hand`   → a whole hand (mine or theirs),
     *  - 'deck' + `${owner}_deck`   → a deck pile (mine or theirs),
     *  - 'opponentHandCard'         → the hand the ACTOR is facing (so: mine
     *                                 when they act, THEIRS when I do),
     *                                 nulled id (privacy), resolved by index,
     *  - 'player'/none              → a summoner portrait (mine or theirs).
     */
    /**
     * Did this cast DECLARE a target?
     *
     * `resolveTargetEl` falls back to a portrait when `targetType` is absent,
     * because attacks and player-targeted spells legitimately arrive that way.
     * A non-targetable spell also arrives with no targetType, so anything that
     * asks the resolver where to draw gets the opponent's summoner — which is
     * why an untargeted cantrip flashed on the enemy portrait. Presentation
     * that only makes sense against a real target asks this first.
     */
    function hasDeclaredSpellTarget(data) {
        if (!data || typeof data !== 'object') return false;
        return Boolean(data.targetType) || Boolean(data.targetId) || Boolean(data.targetPresentation);
    }

    /**
     * WHERE TO DRAW WHEN THE SERVER WOULD NOT NAME THE TARGET.
     *
     * A card in ANY hand, and a card anywhere in a deck, is hidden information,
     * so `actionEvents.js` nulls `targetId` on the broadcast and describes the
     * target with `targetPresentation` instead — `{kind, playerId, handIndex}`.
     * `targetIndex` only survives for the one route a human client sends
     * explicitly (`opponentHandCard`), so every OTHER hand/deck target arrived
     * as `{targetType: 'card', targetId: null}` and the id branches below fell
     * through to `return null`: the strike was skipped and the beat vanished.
     * ("targeting enemy cards in hand no longer has attack animation.
     *   potentially also affects firendly hands/decks?")
     *
     * The presentation is the honest anchor. It names the OWNER and the SLOT,
     * which is precisely what a player can already see on screen — a position
     * in a fan, a deck pile — and never which card is in it, so drawing from it
     * leaks nothing the privacy nulling was protecting.
     */
    function presentationTargetEl(presentation) {
        if (!presentation || typeof presentation !== 'object') return null;
        const { kind, playerId, zone, handIndex } = presentation;
        if (kind === 'hiddenHandCard') {
            const hand = ownerZoneEl(playerId, 'hand');
            if (!hand) return null;
            return (Number.isInteger(handIndex) && hand.children[handIndex]) || hand;
        }
        if (kind === 'zone' && (zone === 'hand' || zone === 'deck')) {
            return ownerZoneEl(playerId, zone);
        }
        if (kind === 'card') {
            const instanceId = typeof presentation.card?.instanceId === 'string'
                ? presentation.card.instanceId
                : '';
            if (!instanceId) return null;
            try {
                return document.querySelector(
                    `.card[data-instance-id="${instanceId.replace(/["\\]/g, '\\$&')}"]`,
                );
            } catch (_) {
                return null;
            }
        }
        return null;
    }

    function resolveTargetEl(data) {
        const presentation = data?.targetPresentation;
        return resolveDeclaredTargetEl(data || {}, presentation)
            || presentationTargetEl(presentation);
    }

    function resolveDeclaredTargetEl({ playerId, targetId, targetType, targetIndex }, presentation) {
        if (targetType === 'deck' && typeof targetId === 'string' && targetId.endsWith('_deck')) {
            return ownerZoneEl(targetId.slice(0, -'_deck'.length), 'deck');
        }
        if (targetType === 'card' && targetId) {
            if (targetId.endsWith('_hand')) {
                return ownerZoneEl(targetId.slice(0, -'_hand'.length), 'hand');
            }
            try {
                return document.querySelector(`.card[data-instance-id="${targetId}"]`);
            } catch (_) {
                return null;
            }
        }
        if (targetType === 'opponentHandCard') {
            // Named from the ACTOR's seat: "the hand of whoever the actor is
            // facing". That is MY hand only while the opponent is acting. This
            // function also serves my own attacks — a drag from my board onto
            // an enemy card-back routes here — and the hardcoded #player-hand
            // made my own strike lunge at my own hand. Resolve by OWNER, the
            // way the summoner branch below already does.
            const side = playerId && playerId === myPlayerId() ? 'opponent' : 'player';
            const hand = document.getElementById(`${side}-hand`);
            if (!hand) return null;
            /* The AI path emits this type with no `targetIndex` (aiEngine.js
               passes null), so take the slot from the presentation when the
               explicit index is missing rather than settling for the whole fan. */
            const index = Number.isInteger(targetIndex)
                ? targetIndex
                : (presentation?.kind === 'hiddenHandCard' ? presentation.handIndex : null);
            return (Number.isInteger(index) && hand.children[index]) || hand;
        }
        if (targetType === 'player' || !targetType) {
            // Resolve by who OWNS the target, not by who is acting: this path
            // also serves my own attacks, where "not the actor" would point at
            // my own portrait when I strike the enemy summoner.
            const me = myPlayerId();
            if (targetId) {
                return document.getElementById(targetId === me ? 'player-portrait' : 'opponent-portrait');
            }
            // No explicit target — "the other player" from the ACTOR's seat.
            return document.getElementById(playerId === me ? 'opponent-portrait' : 'player-portrait');
        }
        return null;
    }

    /**
     * WHICH SIDE OF THE TABLE an element sits on: 'player', 'opponent', or
     * null for something that belongs to neither (an FX node, a detached
     * probe). Read from the ids the battle is built on — the areas, the lanes,
     * the hands, the portraits and the deck piles — and from a card's own
     * `data-zone`, so it holds for every target the resolver above can name.
     */
    function elementSide(el) {
        const zone = el?.dataset?.zone;
        if (typeof zone === 'string') {
            if (zone.startsWith('player-')) return 'player';
            if (zone.startsWith('opponent-')) return 'opponent';
        }
        const host = el?.closest?.(
            '#player-area, #player-board, #player-hand, #player-portrait, #player-deck, '
            + '#opponent-area, #opponent-board, #opponent-hand, #opponent-portrait, #opponent-deck',
        );
        if (!host) return null;
        return host.id.startsWith('player') ? 'player' : 'opponent';
    }

    /**
     * IS THE TARGET ON THE ACTOR'S OWN SIDE?
     *
     * "make targeting of friendly targets with a card have a more positive
     * looking anim. now it looks like a hit regardless." The strike and the
     * lunge were drawn identically whoever owned the target. The relationship
     * decides which beat is drawn, and it is read from the DOM the beat lands
     * on — the one source that cannot disagree with what the player sees —
     * with the payload's declared owner (`targetPresentation.playerId`, or a
     * bare player id target) as the fallback for an element on neither side.
     * With nothing to go on it is a strike, which is what the game drew before.
     *
     * Presentation only: this reads the relationship, it never decides what
     * the action DID. That stays the storyteller's.
     */
    function isFriendlyTarget(data, targetEl) {
        const actor = typeof data?.playerId === 'string' ? data.playerId : '';
        if (!actor) return false;
        const side = elementSide(targetEl);
        if (side) return (side === 'player') === (actor === myPlayerId());
        const owner = data?.targetPresentation?.playerId
            || (data?.targetType === 'player' && typeof data?.targetId === 'string' ? data.targetId : '');
        return Boolean(owner) && owner === actor;
    }

    function enemyHandSource(handIndex) {
        const hand = document.getElementById('opponent-hand');
        if (!hand) return null;
        return (Number.isInteger(handIndex) && hand.children[handIndex]) || hand.firstElementChild || hand;
    }

    /**
     * The specific board slot a summon lands on (renderBoard lays out 5
     * `.card-slot[data-slot-index]` per side). Falls back to the whole board
     * only if that slot can't be resolved — never the board centre by default,
     * or every summon's landing ring would pile up in the middle slot.
     */
    function enemyBoardSlot(boardIndex) {
        const board = document.getElementById('opponent-board');
        if (!board) return null;
        if (Number.isInteger(boardIndex)) {
            try {
                const slot = board.querySelector(`.card-slot[data-slot-index="${boardIndex}"]`);
                if (slot) return slot;
            } catch (_) { /* fall through */ }
        }
        return board;
    }

    function snapshotHasOpponentCard(snapshot, instanceId) {
        const state = snapshot?.gameState || snapshot;
        const cards = state?.opponent?.summonedCards;
        return Boolean(instanceId && Array.isArray(cards) && cards.some(
            (card) => card?.instanceId === instanceId,
        ));
    }

    function restoreSummonBoardCards(entry) {
        entry.hiddenBoardCards?.forEach((opacity, card) => {
            if (card?.dataset?.enemyReplayArrival === entry.instanceId) {
                card.style.opacity = opacity;
                delete card.dataset.enemyReplayArrival;
            }
        });
        entry.hiddenBoardCards?.clear();
    }

    function hideSummonBoardCard(entry, card) {
        if (!card || entry.hiddenBoardCards.has(card)) return;
        entry.hiddenBoardCards.set(card, card.style.opacity);
        card.dataset.enemyReplayArrival = entry.instanceId;
        // Opacity preserves the authoritative card's layout, hit target and
        // accessibility while preventing it from duplicating the traveler.
        card.style.opacity = '0';
    }

    /**
     * The card is being stood in for by a traveler that has not launched yet.
     * Hold it, but only for a bounded wait: the shared art grace, or, while its
     * back is still in the enemy's hand, until the arrival cap counted from the
     * summon. Past that the player is owed the card, and this summon's
     * traveler has missed its window for good.
     */
    function holdForLateArrival(entry, card) {
        hideSummonBoardCard(entry, card);
        clearTimeout(entry.arrivalGraceTimer);
        // From here the hold's own cap is the liveness bound; the preparation
        // deadline would otherwise brand a card still inside its wait.
        clearTimeout(entry.timeoutId);
        const waitMs = entry.handHeld
            ? Math.max(SUMMON_ARRIVAL_GRACE_MS, entry.startedAt + summonArtCapMs() - Date.now())
            : SUMMON_ARRIVAL_GRACE_MS;
        entry.arrivalGraceTimer = setTimeout(() => {
            // Deliberately NOT `showTerminalBoard`: a slow traveler says nothing
            // about the board's own independent art pipeline, which board.js
            // owns and resolves on its own. Branding it `card-summon-art-error`
            // here would be the same lie the typography-only rejection taught.
            cancelPendingSummon(entry);
        }, waitMs);
    }

    /* The card's back stays in the enemy's hand while its traveler prepares
       (see summonArtCapMs); these give it back to the snapshot's count. */
    function holdSummonInHand(entry) {
        if (entry.handHeld || !entry.sourceIsBack || FX?.reducedMotion?.()) return;
        entry.handHeld = true;
        Cards?.holdOpponentDeparture?.(entry.instanceId);
    }

    function releaseSummonFromHand(entry, { rerender = true } = {}) {
        if (!entry?.handHeld) return;
        entry.handHeld = false;
        Cards?.releaseOpponentDeparture?.(entry.instanceId, { rerender });
    }

    function cancelPendingSummon(entry, { showTerminalBoard = false, rerenderHand = true } = {}) {
        if (!entry || entry.cancelled) return;
        entry.cancelled = true;
        clearTimeout(entry.timeoutId);
        clearTimeout(entry.arrivalGraceTimer);
        // The card is revealed on the board in this task: it leaves the hand in it.
        releaseSummonFromHand(entry, { rerender: rerenderHand });
        if (showTerminalBoard) forceTerminalBoardPresentation(entry);
        /* THE ARRIVAL STILL HAPPENS. `FX.playEntrance` skips any card carrying
           this module's marker — the traveler was going to own the landing — so
           a summon abandoned AFTER the board render silently lost its whole
           arrival beat: the card simply appeared. That was already true of every
           post-render cancellation (a rejected settlement, the preparation
           timeout) and the bounded arrival grace makes it the common cold-art
           case, so the beat is delivered here instead, on the real card. */
        const owedLanding = entry.hiddenBoardCards?.size > 0 && !entry.glide;
        restoreSummonBoardCards(entry);
        removePreparationHost(entry.host, entry.contentEl);
        if (owedLanding) {
            const landed = enemyBoardCard(entry.instanceId);
            if (landed) FX?.summonLandingImpact?.(landed, { strength: 0.85 });
        }
        if (pendingSummons.get(entry.instanceId) === entry) {
            pendingSummons.delete(entry.instanceId);
        }
    }

    function finishActiveSummon(entry) {
        if (!entry) return;
        clearTimeout(entry.arrivalGraceTimer);
        clearTimeout(entry.finishTimer);
        clearTimeout(entry.handoffTimer);
        clearTimeout(entry.impactTimer);
        clearTimeout(entry.cueTimer);
        entry.glide?.el?.remove?.();
        restoreSummonBoardCards(entry);
        if (activeSummons.get(entry.instanceId) === entry) {
            activeSummons.delete(entry.instanceId);
        }
    }

    function forceTerminalBoardPresentation(entry) {
        const card = enemyBoardCard(entry.instanceId);
        if (card?.classList.contains('card-summon-art-pending')) {
            card.classList.remove('card-summon-art-pending');
            card.classList.add('card-summon-art-error');
            card.dataset.cardArtPresentation = 'error';
        }
        entry.boardPresentationReady = true;
    }

    function completeMatchedSummon(entry) {
        if (entry.travelComplete && entry.boardPresentationReady) {
            finishActiveSummon(entry);
        }
    }

    function observeMatchedBoardPresentation(entry, targetEl) {
        const transfer = Cards?.transferSettledRasterPaint;
        if (typeof transfer === 'function') {
            /* One frame later, never on the launch tick: the 4.8x oversample
               paints a whole card face onto a canvas SYNCHRONOUSLY, and doing
               that in the same task that starts the glide ate the first
               frames of travel — the hitch at the start of every enemy
               summon's flight. */
            const defer = typeof requestAnimationFrame === 'function'
                ? requestAnimationFrame
                : (fn) => setTimeout(fn, 16);
            defer(() => {
                if (activeSummons.get(entry.instanceId) !== entry) return;
                try {
                    transfer(entry.contentEl, targetEl, {
                        oversample: 4.8,
                        stableGeometry: false,
                    });
                } catch (_) { /* the board's own settlement remains authoritative */ }
            });
        }

        const waitForPresentation = Board?.waitForCardPresentation;
        let presentation = targetEl;
        if (typeof waitForPresentation === 'function') {
            try {
                presentation = waitForPresentation(entry.instanceId);
            } catch (_) {
                presentation = null;
            }
        }
        const settle = (card) => {
            if (activeSummons.get(entry.instanceId) !== entry) return;
            const liveCard = enemyBoardCard(entry.instanceId);
            entry.boardPresentationReady = Boolean(
                card
                && liveCard
                && !liveCard.classList.contains('card-summon-art-pending'),
            );
            if (!entry.boardPresentationReady) forceTerminalBoardPresentation(entry);
            completeMatchedSummon(entry);
        };
        if (presentation && typeof presentation.then === 'function') {
            Promise.resolve(presentation).then(settle, () => settle(null));
        } else {
            settle(presentation);
        }
        entry.handoffTimer = setTimeout(() => {
            if (activeSummons.get(entry.instanceId) !== entry) return;
            forceTerminalBoardPresentation(entry);
            completeMatchedSummon(entry);
        }, SUMMON_HANDOFF_TIMEOUT_MS);
    }

    function startMatchedSummon(entry) {
        if (
            !entry
            || entry.cancelled
            || entry.settled !== true
            || entry.snapshotRendered !== true
        ) return;
        if (!shouldReplay(entry.data.playerId)) {
            cancelPendingSummon(entry);
            return;
        }
        const targetEl = enemyBoardCard(entry.instanceId);
        const to = visibleRect(targetEl);
        if (!targetEl || !to) {
            cancelPendingSummon(entry);
            return;
        }
        /* "same happens for enemy if they summon" — the enemy's traveler is a
           different path from the player's in-person landing, so it needs its
           own trace window. Opt-in and no-op unless enabled. */
        Cards?.beginDescriptionTrace?.('enemy-summon-glide');

        /* THE HOLD IS ARMED BY THE LAUNCH, IN THE LAUNCH'S OWN TASK — never by
           the board render, which is what left the card invisible for 770ms
           with nothing standing in for it (Firefox, owner's 844x280 DPR3 page
           box, `arrival` set and zero travelers in #fx-layer). Nothing can
           paint between this line and glidePlay below, so the card and its
           stand-in never coexist on screen and never both go missing. */
        hideSummonBoardCard(entry, targetEl);
        removePreparationHost(entry.host, entry.contentEl);
        // The back it waited in leaves the hand as the traveler leaves it.
        releaseSummonFromHand(entry);
        let glide = null;
        try {
            glide = FX?.glidePlay?.({
                contentEl: entry.contentEl,
                sourceContainerId: 'opponent-hand',
                targetEl,
                from: entry.from,
                to,
                duration: TRAVEL_DURATION,
                holdAtEnd: true,
                trail: true,
                /* This module hides the authoritative card and removes the
                   traveler itself (finishActiveSummon); the generic snapshot
                   sweep must not delete it mid-flight. */
                ownedLifecycle: true,
            });
        } catch (_) {
            glide = null;
        }
        if (!glide) {
            cancelPendingSummon(entry);
            return;
        }

        clearTimeout(entry.timeoutId);
        clearTimeout(entry.arrivalGraceTimer);
        pendingSummons.delete(entry.instanceId);
        entry.glide = glide;
        entry.boardPresentationReady = false;
        entry.travelComplete = false;
        activeSummons.set(entry.instanceId, entry);
        observeMatchedBoardPresentation(entry, targetEl);
        /* The cue is the TRAVELER'S to schedule. It used to play on the accept
           event, which precedes this launch by however long the face took to
           prepare, so the swell peaked wherever it happened to fall. The glide
           knows its own contact; FX starts the clip one peak-offset ahead of
           it so the loudest instant lands as the card touches the board. */
        entry.cueTimer = FX?.cueSummonContact?.(glide.contactMs, { card: entry.data?.card }) ?? null;
        /* The full landing ceremony, shared with the player's own summon —
           flash, shockwave, dust, entrance rim, neighbour shove, lane dip.
           The lone 0.7 impact ring this used to fire is why the enemy's
           arrival read as "barely not broken" next to yours. One step below
           the player's weight so your own slam still owns the loudest seat. */
        entry.impactTimer = setTimeout(() => {
            const landed = enemyBoardCard(entry.instanceId) || targetEl;
            if (FX?.summonLandingImpact) FX.summonLandingImpact(landed, { strength: 0.85 });
            else FX?.impactAt?.(landed, { strength: 0.7 });
        }, glide.contactMs);
        entry.finishTimer = setTimeout(() => {
            entry.travelComplete = true;
            completeMatchedSummon(entry);
        }, TRAVEL_DURATION);
    }

    /**
     * Stage a board attack. One code path for both players, because
     * `characterActionStarted` is emitted only after the server has validated
     * the action and committed to resolving it: my own accepted attack is as
     * authoritative an observation as the opponent's, and both deserve the
     * same strike. (A drop-time cue would instead fire for attacks the server
     * goes on to reject.)
     *
     * `FX.lungeAttack` owns the whole beat — the attacker's real card moves
     * in person, and the target's ring, kick, knock-back and the single
     * `impact` sound all land together at contact. When the target cannot be
     * resolved to a live element there is NOTHING to show, and this makes no
     * noise about it: a hit sound with no visible hit is a half-presentation
     * that reads as a bug, so the miss is reported to the debug console only.
     */
    function stageAttack(data, sourceEl) {
        const targetEl = resolveTargetEl(data);
        if (!targetEl || !visibleRect(targetEl)) {
            if (window.CLIENT_DEBUG_MODE) {
                console.warn('[EnemyReplay] attack target not on screen; no strike rendered', {
                    characterId: data.characterId,
                    targetType: data.targetType,
                });
            }
            return;
        }
        FX?.lungeAttack?.({
            sourceEl,
            targetEl,
            duration: ATTACK_DURATION,
            // Pre-resolution: the damage of this strike does not exist yet.
            // The attacker's own authoritative mana cost is its mass.
            manaCost: Number(data.card?.manaCost),
            // A Character reaching its OWN side is a touch, not a blow.
            friendly: isFriendlyTarget(data, targetEl),
        });
    }

    /** MY OWN board attack, on the authoritative accept. */
    function onOwnCharacterAction(data) {
        if (!data) return;
        const state = (typeof GameState !== 'undefined' && GameState.get) ? GameState.get() : null;
        if (!state || state.gameStarted !== true) return;
        if (!data.playerId || data.playerId !== state.playerId) return;
        if (document.hidden) return;
        stageAttack(data, ownBoardCard(data.characterId));
    }

    /** The OPPONENT's board attack / ability. */
    function onCharacterAction(data) {
        if (!data || !shouldReplay(data.playerId, { allowDuringTutorial: true })) return;
        stageAttack(data, enemyBoardCard(data.characterId));
    }

    /** Summon: reveal one complete physical card, then carry that exact node
     *  from the indexed hand position to its authoritative board slot. */
    function onSummon(data) {
        if (!data || !shouldReplay(data.playerId)) return;
        const instanceId = data.cardInstanceId || data.card?.instanceId;
        if (!instanceId) return;
        if (
            pendingSummons.has(instanceId)
            || activeSummons.has(instanceId)
            || enemyBoardCard(instanceId)
        ) return;
        const sourceEl = enemyHandSource(data.handIndex);
        const landingEl = enemyBoardSlot(data.boardIndex);
        const from = departureRect(sourceEl);
        const to = visibleRect(landingEl);
        if (!from || !to) return;

        let contentEl = null;
        if (data.card && Cards?.createCardElement) {
            try {
                contentEl = Cards.createCardElement(
                    data.card,
                    `enemy-summon-${data.cardInstanceId || 'character'}`,
                    { rasterReserveScale: 1.2 },
                );
            } catch (_) {
                contentEl = null;
            }
        }
        // A partial event cannot satisfy the physical-card contract. The
        // authoritative snapshot still renders normally; never substitute a
        // face-down back that turns into text halfway through its flight.
        if (!contentEl) return;

        // Card construction is text-first, but an arriving physical object is
        // the narrow asset-loading exception: keep the whole face offscreen
        // until BOTH its art and the matching authoritative board snapshot are
        // settled, then move this SAME node from the pre-render hand geometry.
        // Never resolve handIndex after the snapshot: it then names a different
        // hidden card and would make that innocent back depart as well.
        const host = document.createElement('div');
        host.dataset.enemyReplayPreparing = 'true';
        host.style.position = 'fixed';
        host.style.left = `${from.left}px`;
        host.style.top = `${from.top}px`;
        host.style.width = `${from.width}px`;
        host.style.height = `${from.height}px`;
        host.style.visibility = 'hidden';
        host.style.pointerEvents = 'none';
        contentEl.style.width = '100%';
        contentEl.style.height = '100%';
        host.appendChild(contentEl);
        document.body.appendChild(host);
        preparationHosts.add(host);

        const entry = {
            arrivalGraceTimer: null,
            cancelled: false,
            contentEl,
            data,
            from,
            handHeld: false,
            hiddenBoardCards: new Map(),
            host,
            instanceId,
            settled: false,
            snapshotMatched: false,
            snapshotRendered: false,
            // A face-down back can wait in the hand; a revealed face cannot.
            sourceIsBack: Boolean(sourceEl?.classList?.contains('card-back')),
            startedAt: Date.now(),
            timeoutId: null,
        };
        pendingSummons.set(instanceId, entry);
        entry.timeoutId = setTimeout(
            () => cancelPendingSummon(entry, { showTerminalBoard: true }),
            SUMMON_PREPARATION_TIMEOUT_MS,
        );

        const settlePresentation = Cards?.settleCardFaceForPresentation;
        let settlement = false;
        if (typeof settlePresentation === 'function') {
            try {
                settlement = settlePresentation(contentEl, data.card, {
                    oversample: 1.2,
                    stableGeometry: true,
                });
            } catch (_) {
                // An optional traveler failure says nothing about the board's
                // independent art authority.
                cancelPendingSummon(entry);
                return;
            }
        }
        const finish = (settled) => {
            if (entry.cancelled) return;
            if (!settled || settled.ready !== true) {
                // Only a proven raster failure may classify board art as a
                // terminal error. A font/geometry miss merely suppresses the
                // optional traveler and leaves the authoritative board's
                // loading/ready/error pipeline untouched.
                cancelPendingSummon(entry, {
                    showTerminalBoard: settled?.connected === true
                        && settled?.rasterReady === false,
                });
                return;
            }
            entry.settled = true;
            startMatchedSummon(entry);
        };
        if (settlement && typeof settlement.then === 'function') {
            Promise.resolve(settlement).then(
                finish,
                () => cancelPendingSummon(entry),
            );
        } else {
            finish(settlement);
        }
    }

    /** Spell: the revealed card (full payload) glides to its target and dissolves. */
    /* Only a cast that declared a target travels. An untargeted cast has
       nothing to fly AT, and a flight into EITHER portrait reads as a summoner
       target: resolveTargetEl's fallback aimed it at mine, and the later
       "return to its caster" flew it into theirs ("enemy untargeted spells
       appear targeted on summoner"). It only floats: the SpellAnnounce float
       presents the card and the socket handler's board-wide surge is the
       spell's acknowledgement. Owner, 27 Oct: "Does it float? I think that's
       kind of more like what should happen". */
    function spellGlideTarget(data) {
        return hasDeclaredSpellTarget(data) ? resolveTargetEl(data) : null;
    }

    /* Where an untargeted cast's SURGE erupts from: the spell card itself —
       "make it happen on the spell card instead of the summoner". Priority:
       the live card element (your own cast: the card is still in your hand
       when the started-event lands), then the announce float's card face (an
       enemy cast presents the revealed card there), then the caster's
       portrait as the last honest anchor. spellSurge re-measures at fire
       time and falls back to board centre if the anchor left the DOM. */
    function surgeOriginEl(data) {
        const inst = typeof data?.cardInstanceId === 'string' ? data.cardInstanceId.trim() : '';
        /* A spell the player DRAGGED onto the battlefield was spent where it
           was let go (drag to play, 2026-09-24): its seat in the hand is empty,
           so the surge starts from the point the drag remembers. */
        if (inst && data?.playerId === myPlayerId()) {
            try {
                const castPoint = CardDrag?.castPointOf?.(inst);
                if (castPoint) return castPoint;
            } catch (_) { /* fall through to the card itself */ }
        }
        if (inst) {
            try {
                const held = document.querySelector(
                    `.card[data-instance-id="${inst.replace(/["\\]/g, '\\$&')}"]`,
                );
                if (held) return held;
            } catch (_) { /* fall through */ }
        }
        const floats = document.querySelectorAll('.spell-announce-card-wrap .card');
        if (floats.length) return floats[floats.length - 1];
        return document.getElementById(
            data?.playerId === myPlayerId() ? 'player-portrait' : 'opponent-portrait',
        );
    }

    function onSpellCast(data) {
        if (!data || !shouldReplay(data.playerId)) return;
        const targetEl = spellGlideTarget(data);
        if (!targetEl) return;
        const sourceEl = enemyHandSource(null);
        const from = departureRect(sourceEl);
        const to = visibleRect(targetEl);
        if (!from || !to) return;

        // The event carries the resolved spell card — render it for the glide
        // so the enemy visibly plays THAT card. Fall back to a hand-back clone.
        let contentEl = null;
        if (data.card && Cards?.createCardElement) {
            try {
                contentEl = Cards.createCardElement(
                    data.card,
                    `enemy-cast-${data.cardInstanceId || 'spell'}`,
                    { rasterReserveScale: 1.2 },
                );
            } catch (_) {
                contentEl = null;
            }
        }

        const playGlide = (preparedContent = null) => {
            const liveTarget = spellGlideTarget(data);
            const liveSource = enemyHandSource(null);
            const liveFrom = departureRect(liveSource);
            const liveTo = visibleRect(liveTarget);
            if (!liveFrom || !liveTo) return;
            const glide = FX?.glidePlay?.(preparedContent
                ? {
                    contentEl: preparedContent,
                    targetEl: liveTarget,
                    from: liveFrom,
                    to: liveTo,
                    duration: TRAVEL_DURATION,
                    trail: true,
                }
                : {
                    sourceEl: liveSource,
                    targetEl: liveTarget,
                    from: liveFrom,
                    to: liveTo,
                    duration: TRAVEL_DURATION,
                    trail: true,
                });
            // The back the cast flew out of stops holding a place in the fan.
            // Which back that is was always a fiction (the server nulls the
            // index for privacy and every back is interchangeable) — this just
            // makes the fiction consistent with the card the player watched
            // leave. Only when a clone is genuinely in flight.
            if (glide) EnemyFan?.markDeparting?.(liveSource);
        };
        if (!contentEl) {
            playGlide();
            return;
        }

        // Give the fresh face its exact glide geometry while keeping it
        // unpainted. The shared presentation gate can now materialize physical
        // pixels before FX receives the same node and begins motion.
        const generation = replayGeneration;
        const host = document.createElement('div');
        host.dataset.enemyReplayPreparing = 'true';
        host.style.position = 'fixed';
        host.style.left = `${from.left}px`;
        host.style.top = `${from.top}px`;
        host.style.width = `${from.width}px`;
        host.style.height = `${from.height}px`;
        host.style.visibility = 'hidden';
        host.style.pointerEvents = 'none';
        contentEl.style.width = '100%';
        contentEl.style.height = '100%';
        host.appendChild(contentEl);
        document.body.appendChild(host);
        preparationHosts.add(host);

        const settlePresentation = Cards?.settleCardRasterForPresentation;
        const settlement = typeof settlePresentation === 'function'
            ? settlePresentation(contentEl, data.card, {
                oversample: 1.2,
                stableGeometry: true,
            })
            : true;
        let finished = false;
        let capTimer = null;
        const finish = (settled) => {
            if (finished) return;
            finished = true;
            clearTimeout(capTimer);
            const current = generation === replayGeneration
                && host.isConnected
                && shouldReplay(data.playerId);
            removePreparationHost(host, contentEl);
            if (!current) return;
            if (settled === true) playGlide(contentEl);
            else playGlide();
        };
        if (settlement && typeof settlement.then === 'function') {
            /* The same arrival cap as a summon: a face still loading by then
               flies as the hand's back, never half painted, and never so late
               that the cast has long resolved without it. */
            capTimer = setTimeout(() => finish(false), summonArtCapMs());
            Promise.resolve(settlement).then(
                finish,
                () => finish(false),
            );
        } else {
            finish(settlement === true);
        }
    }

    /** Before a board rebuild, preserve only the pending summon that this
     *  authoritative snapshot actually commits. Every older arrival is stale. */
    function onSnapshot(nextState) {
        replayGeneration += 1;
        /* An unrelated update mid-flight must not kill a live summon glide —
           that was the "mid-move freeze": mana ticks and story updates land
           inside the 552ms travel constantly. A summon whose card is still in
           the snapshot re-binds to the rebuilt board node after the render;
           only a summon whose card VANISHED finishes here. */
        activeSummons.forEach((entry) => {
            if (snapshotHasOpponentCard(nextState, entry.instanceId)) {
                entry.awaitingRebind = true;
            } else {
                finishActiveSummon(entry);
            }
        });
        /* THE SAME RULE THE ACTIVE SUMMONS ABOVE USE: only a card that VANISHED
           ends the summon. This asked for the FIRST matching snapshot
           (`!entry.snapshotMatched && ...`) and cancelled on every one after
           it — including snapshots that plainly still carried the card.
           Preparation runs until the card face settles, up to
           SUMMON_PREPARATION_TIMEOUT_MS, and mana ticks, story updates and turn
           state land inside a window that long constantly. So the second update
           to arrive removed the traveller's host, the face settle then resolved
           `{connected: false}`, and the glide was cancelled: the enemy
           Character simply appeared, with no animation and nothing logged.
           That is the mid-move freeze the block above was written for, one
           stage earlier. Caught as an intermittent failure of
           game-juice.spec.js "enemy live-play glide survives its immediate
           snapshot", where the readiness gate reported
           `{connected: false, rasterReady: false, textReady: false, ready: false}`
           and the preparation host was gone 105ms in — nowhere near the cap. */
        const preservedHosts = new Set();
        [...pendingSummons.values()].forEach((entry) => {
            if (!snapshotHasOpponentCard(nextState, entry.instanceId)) {
                // The render this snapshot runs next draws the hand without it.
                cancelPendingSummon(entry, { rerenderHand: false });
                return;
            }
            entry.snapshotMatched = true;
            preservedHosts.add(entry.host);
            /* Its face is still on its way: the render this snapshot runs next
               keeps the card's back in the enemy's hand until it can fly. */
            if (!entry.settled) holdSummonInHand(entry);
        });
        clearPreparationHosts(preservedHosts);
        FX?.clearHeldGlides?.();
    }

    /** After the matching board rebuild, hide only that authoritative copy
     *  and hand it off from the fully prepared traveler at settlement. */
    function onSnapshotRendered(nextState) {
        // Live glides whose card survived the snapshot: the board render just
        // rebuilt their authoritative node fully visible next to the traveler.
        // Hide the new node too; the handoff restores it (hiddenBoardCards
        // carries every node this summon ever hid, dead ones inert).
        activeSummons.forEach((entry) => {
            if (!entry.awaitingRebind) return;
            entry.awaitingRebind = false;
            const rebuilt = enemyBoardCard(entry.instanceId);
            if (rebuilt) hideSummonBoardCard(entry, rebuilt);
        });
        [...pendingSummons.values()].forEach((entry) => {
            if (
                !entry.snapshotMatched
                || !snapshotHasOpponentCard(nextState, entry.instanceId)
            ) {
                cancelPendingSummon(entry);
                return;
            }
            const authoritativeCard = enemyBoardCard(entry.instanceId);
            if (!authoritativeCard) {
                cancelPendingSummon(entry);
                return;
            }
            entry.snapshotRendered = true;
            /* Launch FIRST. A traveler whose face is already settled takes over
               in this same task, so the card is never visible next to it and
               never hidden without it. Only a traveler that is still decoding
               needs the bounded hold below. */
            startMatchedSummon(entry);
            if (pendingSummons.get(entry.instanceId) === entry) {
                holdForLateArrival(entry, authoritativeCard);
            }
        });
    }

    /** Tear down every cross-snapshot presentation lease. Resume, rematch,
     *  lobby return, and scope loss must not leave a hidden authoritative card,
     *  a zombie traveler, or a late raster promise in the next game. */
    function reset() {
        replayGeneration += 1;
        [...activeSummons.values()].forEach(finishActiveSummon);
        // The next game's first render draws its own hands.
        [...pendingSummons.values()].forEach((entry) => cancelPendingSummon(entry, { rerenderHand: false }));
        clearPreparationHosts();
        FX?.clearHeldGlides?.();
    }

    return {
        shouldReplay,
        // One target resolver for the whole client. The spell-impact beat needs
        // the same owner-relative resolution this module already got right.
        hasDeclaredSpellTarget,
        isFriendlyTarget,
        resolveTargetEl,
        surgeOriginEl,
        onCharacterAction,
        onOwnCharacterAction,
        onSummon,
        onSpellCast,
        onSnapshot,
        onSnapshotRendered,
        reset,
    };
})();

export { EnemyReplay };
